import { useEffect, useState } from "react";import { useParams, Link } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSectionTheme } from "@/context/BrandingContext";
import { toast } from "sonner";
import { Briefcase, MapPin, FileText, ChevronLeft, Mail, Phone, MessageSquare, MessageCircle, UserPlus, Copy, Eye, History, FileDiff, Printer, Calendar as CalIcon, Sparkles, AlertTriangle, TrendingUp, Send, Download, MoreHorizontal, X } from "lucide-react";
import ScoreBadge from "@/components/ScoreBadge";
import CandidateVoteBar from "@/components/CandidateVoteBar";
import UnfitBadge from "@/components/UnfitBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CandidateRatings from "@/components/candidate/CandidateRatings";
import CandidateInterviews from "@/components/candidate/CandidateInterviews";
import CandidateNotes from "@/components/candidate/CandidateNotes";
import CandidateVersions from "@/components/candidate/CandidateVersions";
import InterviewQA from "@/components/candidate/InterviewQA";
import ResumeDiffPanel from "@/components/candidate/ResumeDiffPanel";
import CandidateFollowups from "@/components/candidate/CandidateFollowups";
import CandidateCompensation from "@/components/candidate/CandidateCompensation";
import CandidateAssignments from "@/components/candidate/CandidateAssignments";
import CandidateOnlinePresence from "@/components/candidate/CandidateOnlinePresence";
import CandidatePortfolio from "@/components/candidate/CandidatePortfolio";
import UnifiedTimeline from "@/components/candidate/UnifiedTimeline";
import { AssignJobsButton, AddToListButton } from "@/components/candidate/CandidateActions";
import ScreeningProfileEditor from "@/components/candidate/ScreeningProfileEditor";
import { ScreeningScorecardStandalone } from "@/components/candidate/ScreeningScorecard";
import BackgroundCheck from "@/components/candidate/BackgroundCheck";
import PersonalDocuments from "@/components/candidate/PersonalDocuments";
import RedFlagSummary from "@/components/candidate/RedFlagSummary";
import CandidateAccordion from "@/components/candidate/CandidateAccordion";
import { AnalysisPanel, RenamableCandidateName, RecruiterSummaryRow } from "@/components/CandidateProfileDialog";
import ComposeEmailDialog from "@/components/ComposeEmailDialog";
import MessageThreadDialog from "@/components/MessageThreadDialog";
import InterviewDetailDialog from "@/components/InterviewDetailDialog";
import ResumePreviewDialog from "@/components/ResumePreviewDialog";
import TimelineList from "@/components/TimelineList";
import ActivityFeed from "@/components/ActivityFeed";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useDocsGateStageChange } from "@/lib/useDocsGateStageChange";

export default function CandidateDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    useSectionTheme("candidate_profile");
    const [c, setC] = useState(null);
    const [lists, setLists] = useState([]);
    const [stages, setStages] = useState([]);
    const [showMessage, setShowMessage] = useState(false);
    const [showResume, setShowResume] = useState(false);
    const [showCompose, setShowCompose] = useState(false);
    const [unreadMsg, setUnreadMsg] = useState(0);
    const [inviteResult, setInviteResult] = useState(null);
    const [inviting, setInviting] = useState(false);
    const [viewMode, setViewMode] = useState(() => {
        try { return localStorage.getItem("pw-candidate-view-mode") || "scroll"; } catch { return "scroll"; }
    });
    useEffect(() => {
        try { localStorage.setItem("pw-candidate-view-mode", viewMode); } catch { /* ignore */ }
    }, [viewMode]);

    const load = async () => {
        try {
            const { data } = await api.get(`/candidates/${id}`);
            setC(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const loadLists = async () => {
        try {
            const { data } = await api.get(`/hiring-lists/by-candidate/${id}`);
            setLists(data || []);
        } catch { /* ignore */ }
    };
    const loadStages = async () => {
        try {
            const { data } = await api.get("/company/pipeline-stages");
            setStages(data || []);
        } catch { /* ignore */ }
    };
    useEffect(() => { load(); loadLists(); loadStages(); /* eslint-disable-next-line */ }, [id]);

    // Phase 155 — unread count for this candidate's thread (if any).
    const loadUnread = async () => {
        if (!id) return;
        try {
            const { data } = await api.get("/messages/threads", { params: { status: "all" } });
            const mine = (data || []).find((t) => t.candidate_id === id);
            setUnreadMsg(mine?.unread_count || 0);
        } catch { /* silent */ }
    };
    useEffect(() => { loadUnread(); /* eslint-disable-next-line */ }, [id]);
    // When the thread dialog closes, refresh counts (both local + top-bar).
    useEffect(() => {
        if (!showMessage) {
            loadUnread();
            try { window.dispatchEvent(new Event("pw-messages-refresh")); } catch { /* ignore */ }
        }
        // eslint-disable-next-line
    }, [showMessage]);

    // Doc Freshness gate — reused across full CandidateDetail page
    const { changeStage: docsGateChangeStage, modal: docsGateModal } = useDocsGateStageChange();
    // Snapshot of missing-docs evaluation for the header banner (nice-to-have,
    // silent when the gate is disabled or nothing missing).
    const [docsGate, setDocsGate] = useState(null);
    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                const { data } = await api.get(`/candidates/${id}/doc-freshness`);
                setDocsGate(data);
            } catch { /* silent */ }
        })();
    }, [id, c?.applications?.length]);

    const updateStage = async (appId, stage) => {
        const app = (c?.applications || []).find((a) => a.id === appId);
        const res = await docsGateChangeStage(appId, stage, {
            candidate_id: id,
            candidate_name: c?.name || "",
        });
        if (res?.ok) {
            setC((cur) => ({
                ...cur,
                applications: (cur?.applications || []).map((a) => a.id === appId ? { ...a, stage } : a),
            }));
            toast.success("Stage updated");
        }
    };

    if (!c) return <div className="p-8 font-mono text-sm text-muted-foreground">Loading...</div>;

    const isOwner = user?.role === "employer";
    const isInterviewer = user?.role === "interviewer";
    const tabs = [
        ["analysis", "AI Analysis"],
        ["overview", "Overview"],
        ...(isInterviewer ? [] : [["compensation", "Compensation"]]),
        ...(isInterviewer ? [] : [["bgv", "BGV"]]),
        ["followups", "Follow-ups"],
        ["ratings", "Ratings"],
        ["interviews", "Interviews"],
        ["qa", "Interview Q&A"],
        ["notes", "Notes"],
        ["versions", "Resume"],
        ["diff", "Resume diff"],
        ["jobs", "Applications"],
        ["activity", "Activity"],
    ];

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-5">
            <Link to="/candidates" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-candidates">
                <ChevronLeft className="w-4 h-4" /> All candidates
            </Link>

            {/* Mobile-first smart header — Phase 155 redesign:
             *  (1) Identity block up top (name · title · location · exp)
             *  (2) Big primary comms row (Call · WhatsApp · Email · Message)
             *  (3) Scores + salary + lists
             *  (4) Secondary actions (Add to job / list / Schedule / Resume / …)
             *  Verbose / rarely-used actions tuck into a "More" expander on mobile.
             */}
            <header className="border border-border bg-white" data-testid="cand-header">
                <div className="px-3 sm:px-4 py-3 sm:py-4 space-y-3">
                    {/* Identity block */}
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <RenamableCandidateName candidate={c} loading={!c} onRenamed={load} />
                            {c.seeker_id && <span className="text-[10px] font-mono uppercase tracking-widest border border-emerald-300 bg-emerald-50 text-emerald-700 px-1.5 py-0.5" data-testid="cand-portal-linked">✓ Portal</span>}
                            <UnfitBadge unfit={c.unfit} reason={c.unfit_reason} size="md" />
                        </div>
                        <div className="flex items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-foreground/80 mt-1 flex-wrap">
                            {c.current_title && <span className="inline-flex items-center gap-1 font-medium"><Briefcase className="w-3.5 h-3.5" /> {c.current_title}{c.current_company ? <span className="text-foreground/60"> @ {c.current_company}</span> : ""}</span>}
                            {c.location && <span className="inline-flex items-center gap-1 text-foreground/70"><MapPin className="w-3.5 h-3.5" /> {c.location}</span>}
                        </div>
                        <RecruiterSummaryRow candidate={c} onSaved={load} />
                        {c.email_bounced && (
                            <div
                                className="mt-2 border border-rose-400 bg-rose-50 text-rose-800 px-2.5 py-1.5 text-xs font-medium flex items-start gap-2"
                                data-testid="cand-email-bounced-banner"
                            >
                                <span aria-hidden>⚠</span>
                                <div className="min-w-0 flex-1">
                                    <div className="font-bold">Email bounced — this address is dead.</div>
                                    <div className="text-[11px] font-mono text-rose-700/90 mt-0.5 truncate" title={c.email_bounce_reason}>
                                        {c.email_bounce_status === "complained" ? "Marked as spam. " : ""}
                                        {c.email_bounce_reason || "Resend reported the message as undeliverable."}
                                        {c.email_bounce_at ? ` · ${new Date(c.email_bounce_at).toLocaleString()}` : ""}
                                    </div>
                                </div>
                            </div>
                        )}
                        <HeaderStatStrip candidate={c} firstApp={(c.applications || [])[0]} />
                        {(c.applications || []).length > 0 && (c.applications[0]?.id) && (
                            <div className="mt-2" data-testid="cand-header-votebar">
                                <CandidateVoteBar applicationId={c.applications[0].id} size="sm" />
                            </div>
                        )}
                        {lists.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                {lists.map((l) => (
                                    <Link key={l.id} to={`/lists/${l.id}`} className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border" style={{ borderColor: l.color, color: l.color }}>
                                        {l.name}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Primary comms — large tap targets on mobile */}
                    <PrimaryCommsRow
                        c={c}
                        unreadMsg={unreadMsg}
                        onMessage={() => setShowMessage(true)}
                        onCompose={() => setShowCompose(true)}
                    />

                    {/* Secondary actions */}
                    <SecondaryActionsRow
                        c={c}
                        onResume={() => setShowResume(true)}
                        onLoaded={load}
                        onListsLoaded={loadLists}
                        inviting={inviting}
                        setInviting={setInviting}
                        setInviteResult={setInviteResult}
                    />

                    {/* More actions (expandable on mobile, inline on desktop) */}
                    <MoreActionsRow c={c} onLoaded={load} />
                </div>
            </header>

            {/* Doc-freshness banner — visible only if the candidate is at a
             *  gate stage AND has missing employment docs. Cheap silent-on-clear. */}
            <RedFlagSummary candidate={c} />
            {docsGate?.applications_at_gate?.length > 0 && docsGate?.is_pending && (
                <div
                    className="border border-amber-300 bg-amber-50 rounded p-3 flex flex-col sm:flex-row items-start sm:items-center gap-3"
                    data-testid="candidate-docs-gate-banner"
                >
                    <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-sm min-w-0 flex-1">
                        <div className="font-semibold text-amber-900">
                            {docsGate.missing.length} document{docsGate.missing.length === 1 ? "" : "s"} pending
                            {" "}— candidate is at{" "}
                            {docsGate.applications_at_gate.map((a) => a.stage).join(", ")}.
                        </div>
                        <div className="text-xs text-amber-900/80 mt-0.5">
                            Missing:{" "}
                            {docsGate.missing.map((m, i) => (
                                <span key={i}>{m.slot_label}{i < docsGate.missing.length - 1 ? ", " : ""}</span>
                            ))}. Attach them below — or ask the candidate to upload directly.
                        </div>
                    </div>
                    <Button
                        size="sm"
                        variant="default"
                        onClick={async () => {
                            try {
                                const { data } = await api.post(`/candidates/${c.id}/doc-freshness/upload-invite`, {});
                                toast.success(`Invite sent to ${data.sent_to}`);
                                // Copy link to clipboard for convenience
                                try {
                                    await navigator.clipboard.writeText(data.upload_url);
                                    toast.info("Upload link also copied to clipboard");
                                } catch { /* clipboard unavailable */ }
                            } catch (err) { toast.error(formatApiError(err)); }
                        }}
                        data-testid="candidate-docs-gate-request-btn"
                    >
                        <Send className="h-4 w-4 mr-1" /> Request from candidate
                    </Button>
                </div>
            )}

            <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} />

            {viewMode === "scroll" ? (
                <CandidateAccordion
                    candidate={c}
                    focusedApp={null}
                    qaApp={null}
                    onReloaded={load}
                    currentUserId={user?.id}
                    isOwner={user?.role === "employer"}
                    storageKey={`page-${c.id}`}
                />
            ) : (
            <Tabs defaultValue="analysis" className="space-y-6">
                <TabsList
                    className="rounded-none bg-white/95 backdrop-blur-sm p-0 border-b border-border h-auto gap-0 w-full justify-start overflow-x-auto flex-nowrap sticky top-0 z-30 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
                    data-testid="cpd-tabs-list"
                >
                    {tabs.map(([v, l]) => {
                        // Mirror the testids used by CandidateProfileDialog so automation
                        // can target the same selectors on both surfaces.
                        const cpdAlias = { compensation: "comp", versions: "resume" }[v] || v;
                        return (
                            <TabsTrigger
                                key={v}
                                value={v}
                                data-testid={`cpd-tab-${cpdAlias}`}
                                data-testid-legacy={`tab-${v}`}
                                className="rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-ikb data-[state=active]:text-ikb data-[state=active]:shadow-none data-[state=active]:bg-transparent px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium whitespace-nowrap shrink-0"
                            >
                                {l}
                            </TabsTrigger>
                        );
                    })}
                </TabsList>

                <TabsContent value="analysis" className="mt-2">
                    <AnalysisPanel candidate={c} apps={c.applications || []} focusedApp={null} onReloaded={load} />
                </TabsContent>

                <TabsContent value="overview" className="space-y-6 sm:space-y-8 mt-2">
                    {!isInterviewer && (
                        <ScreeningScorecardStandalone profile={c.screening_profile || {}} />
                    )}
                    {c.ai_summary && (
                        <section>
                            <div className="text-xs uppercase tracking-widest font-mono text-ikb mb-2">AI Summary</div>
                            <p className="text-base sm:text-lg leading-relaxed">{c.ai_summary}</p>
                        </section>
                    )}
                    <section className="grid grid-cols-3 gap-px bg-border">
                        <div className="bg-white p-3 sm:p-5">
                            <div className="text-[9px] sm:text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Experience</div>
                            <div className="font-display font-black text-xl sm:text-3xl mt-1">{c.total_experience_years || 0}<span className="text-muted-foreground text-xs sm:text-base"> yrs</span></div>
                        </div>
                        <div className="bg-white p-3 sm:p-5">
                            <div className="text-[9px] sm:text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Skills tracked</div>
                            <div className="font-display font-black text-xl sm:text-3xl mt-1">{(c.skills || []).length}</div>
                        </div>
                        <div className="bg-white p-3 sm:p-5">
                            <div className="text-[9px] sm:text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Applied to</div>
                            <div className="font-display font-black text-xl sm:text-3xl mt-1">{(c.applications || []).length}<span className="text-muted-foreground text-xs sm:text-base"> jobs</span></div>
                        </div>
                    </section>
                    {(c.skills || []).length > 0 && (
                        <section>
                            <h3 className="font-display font-bold text-sm uppercase tracking-widest text-muted-foreground mb-3">Skills</h3>
                            <div className="flex flex-wrap gap-2">
                                {c.skills.map((s) => <span key={s} className="text-sm px-2.5 py-1 border border-border bg-white font-medium">{s}</span>)}
                            </div>
                        </section>
                    )}

                    {/* AI-inferred Hard Skills Snapshot — surfaces what the candidate
                        likely knows based on resume+role context. Helps non-technical
                        recruiters spot tech-debt candidates and craft sharper interview
                        questions. Populated by ai_service.enrich_candidate_hard_skills
                        during resume parse. */}
                    {((c.inferred_hard_skills || []).length > 0 || (c.outdated_skill_signals || []).length > 0 || (c.modern_must_knows_missing || []).length > 0) && (
                        <section data-testid="hard-skills-snapshot">
                            <div className="flex items-center gap-2 mb-3">
                                <Sparkles className="w-4 h-4 text-violet-600" />
                                <h3 className="font-display font-bold text-sm uppercase tracking-widest text-foreground">Hard skills snapshot</h3>
                                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/50">AI-inferred</span>
                            </div>
                            <div className="grid md:grid-cols-3 gap-3">
                                {/* Inferred skills — violet */}
                                <div className="border border-violet-200 bg-violet-50/40" data-testid="hss-inferred">
                                    <div className="px-3 py-2 border-b border-violet-200 bg-violet-50 flex items-center justify-between">
                                        <span className="text-[10px] font-mono uppercase tracking-widest text-violet-800 font-semibold">Likely possesses</span>
                                        <span className="text-[9px] font-mono text-violet-700/70">{(c.inferred_hard_skills || []).length}</span>
                                    </div>
                                    {(c.inferred_hard_skills || []).length === 0 ? (
                                        <div className="px-3 py-3 text-[11px] text-foreground/40">No inferred skills yet — re-parse the resume to refresh.</div>
                                    ) : (
                                        <ul className="divide-y divide-violet-100">
                                            {(c.inferred_hard_skills || []).map((it, i) => (
                                                <li key={`${it.skill}-${i}`} className="px-3 py-2" data-testid={`hss-inf-${i}`}>
                                                    <div className="text-sm font-semibold text-violet-900">{it.skill}</div>
                                                    {it.reason && <div className="text-[11px] text-foreground/70 mt-0.5 leading-snug">{it.reason}</div>}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                {/* Modern must-knows missing — amber */}
                                <div className="border border-amber-200 bg-amber-50/40" data-testid="hss-modern">
                                    <div className="px-3 py-2 border-b border-amber-200 bg-amber-50 flex items-center justify-between">
                                        <span className="text-[10px] font-mono uppercase tracking-widest text-amber-800 font-semibold inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Modern must-knows missing</span>
                                        <span className="text-[9px] font-mono text-amber-700/70">{(c.modern_must_knows_missing || []).length}</span>
                                    </div>
                                    {(c.modern_must_knows_missing || []).length === 0 ? (
                                        <div className="px-3 py-3 text-[11px] text-foreground/40">No modern gaps flagged.</div>
                                    ) : (
                                        <ul className="divide-y divide-amber-100">
                                            {(c.modern_must_knows_missing || []).map((s, i) => (
                                                <li key={`${s}-${i}`} className="px-3 py-2 text-sm text-amber-900" data-testid={`hss-mod-${i}`}>{s}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                {/* Outdated signals — rose */}
                                <div className="border border-rose-200 bg-rose-50/40" data-testid="hss-outdated">
                                    <div className="px-3 py-2 border-b border-rose-200 bg-rose-50 flex items-center justify-between">
                                        <span className="text-[10px] font-mono uppercase tracking-widest text-rose-800 font-semibold inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Outdated signals</span>
                                        <span className="text-[9px] font-mono text-rose-700/70">{(c.outdated_skill_signals || []).length}</span>
                                    </div>
                                    {(c.outdated_skill_signals || []).length === 0 ? (
                                        <div className="px-3 py-3 text-[11px] text-foreground/40">No outdated tech detected.</div>
                                    ) : (
                                        <ul className="divide-y divide-rose-100">
                                            {(c.outdated_skill_signals || []).map((s, i) => (
                                                <li key={`${s}-${i}`} className="px-3 py-2 text-sm text-rose-900" data-testid={`hss-out-${i}`}>{s}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                            {c.hard_skills_enriched_at && (
                                <div className="text-[10px] font-mono text-foreground/40 mt-2">Last enriched: {new Date(c.hard_skills_enriched_at).toLocaleString()}</div>
                            )}
                        </section>
                    )}
                    {(c.experience?.length > 0 || c.education?.length > 0) && (
                        <UnifiedTimeline candidate={c} onNotesRefresh={load} />
                    )}
                    {(c.certifications || []).length > 0 && (
                        <section>
                            <h3 className="font-display font-bold text-sm uppercase tracking-widest text-foreground mb-3">Certifications</h3>
                            <div className="space-y-2">
                                {c.certifications.map((cert, i) => (
                                    <div key={i} className="border border-border bg-white p-4 flex items-center justify-between flex-wrap gap-2">
                                        <div>
                                            <div className="font-medium">{cert.name || "—"}</div>
                                            <div className="text-sm text-foreground/80">{cert.issuer || ""}{cert.credential_id ? ` · ${cert.credential_id}` : ""}</div>
                                        </div>
                                        <span className="font-mono text-xs text-foreground/80">
                                            {cert.issued_at || ""}{cert.expires_at ? ` → ${cert.expires_at}` : ""}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    <CandidateAssignments candidateId={c.id} />

                    {!isInterviewer && (
                        <CandidateOnlinePresence candidateId={c.id} />
                    )}

                    {!isInterviewer && (
                        <CandidatePortfolio candidateId={c.id} />
                    )}

                    {!isInterviewer && (
                        <ScreeningProfileEditor
                            candidateId={c.id}
                            initial={c.screening_profile || {}}
                            initialMeta={c.screening_profile_meta || {}}
                            onSaved={(updated) => setC((cur) => ({
                                ...cur,
                                screening_profile: updated?.screening_profile,
                                screening_profile_meta: updated?.screening_profile_meta,
                            }))}
                        />
                    )}
                </TabsContent>

                <TabsContent value="followups"><CandidateFollowups candidateId={c.id} /></TabsContent>
                <TabsContent value="compensation"><CandidateCompensation candidateId={c.id} /></TabsContent>
                <TabsContent value="bgv" data-testid="tab-bgv-content"><BackgroundCheck candidateId={c.id} /></TabsContent>
                <TabsContent value="ratings"><CandidateRatings candidateId={c.id} currentUserId={user?.id} candidateSkills={c.skills || []} /></TabsContent>
                <TabsContent value="interviews"><CandidateInterviews candidateId={c.id} /></TabsContent>
                <TabsContent value="qa">
                    {(c.applications || []).length === 0 ? (
                        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="qa-no-apps">
                            Assign the candidate to a job first — interview Q&amp;A is per application.
                        </div>
                    ) : (
                        <CandidateQAPicker apps={c.applications} />
                    )}
                </TabsContent>
                <TabsContent value="notes"><CandidateNotes candidateId={c.id} currentUserId={user?.id} isOwner={isOwner} /></TabsContent>
                <TabsContent value="versions"><CandidateVersions candidateId={c.id} onUpdated={load} /></TabsContent>
                <TabsContent value="diff"><ResumeDiffPanel candidateId={c.id} /></TabsContent>

                <TabsContent value="jobs">
                    {(c.applications || []).length === 0 ? (
                        <div className="border border-dashed border-border p-8 text-center text-sm text-foreground/80">Not assigned to any jobs yet.</div>
                    ) : (
                        <div className="border border-border bg-white divide-y divide-border">
                            {c.applications.map((a) => (
                                <div key={a.id} data-testid={`app-row-${a.id}`} className="flex items-center justify-between p-4 hover:bg-secondary gap-4">
                                    <Link to={`/jobs/${a.job_id}`} className="flex-1 min-w-0">
                                        <div className="font-medium">{a.job_title || "Job"}</div>
                                        <div className="text-xs text-foreground/80 font-mono">Applied {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}{a.source ? ` · ${a.source}` : ""}</div>
                                    </Link>
                                    {stages.length > 0 && (
                                        <select
                                            value={a.stage || "Applied"}
                                            onChange={(e) => updateStage(a.id, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-xs font-mono uppercase tracking-widest border border-border px-2 py-1.5 bg-white"
                                            data-testid={`app-stage-${a.id}`}
                                        >
                                            {stages.map((s) => (
                                                <option key={s.key} value={s.key}>{s.label}</option>
                                            ))}
                                        </select>
                                    )}
                                    <CandidateVoteBar applicationId={a.id} size="sm" />
                                    <ScoreBadge score={a.match_score || 0} size="sm" />
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="activity">
                    <ActivityFeed targetType="candidate" targetId={c.id} />
                </TabsContent>
            </Tabs>
            )}

            {c.resume_filename && (
                <ResumePreviewDialog
                    open={showResume}
                    onClose={() => setShowResume(false)}
                    candidate={c}
                />
            )}

            {showMessage && (
                <MessageThreadDialog candidate={c} onClose={() => setShowMessage(false)} />
            )}

            {c?.email && (
                <ComposeEmailDialog
                    open={showCompose}
                    onClose={() => setShowCompose(false)}
                    candidate={c ? { ...c, _viewer_name: user?.name } : null}
                />
            )}

            <Dialog open={!!inviteResult} onOpenChange={(o) => { if (!o) setInviteResult(null); }}>
                <DialogContent className="rounded-none max-w-lg" data-testid="invite-result-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display font-bold text-2xl">
                            {inviteResult?.already_registered ? "Already in the portal" : "Invite ready"}
                        </DialogTitle>
                    </DialogHeader>
                    {inviteResult?.already_registered ? (
                        <p className="text-sm text-muted-foreground">{inviteResult?.message}</p>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Send this link to <strong>{c?.email}</strong>. It works for 14 days, lets them
                                set a password, and links every existing profile for that email to their new portal account — so no duplicates are created.
                            </p>
                            <div className="border border-border bg-secondary p-3 font-mono text-xs break-all" data-testid="invite-claim-url">
                                {inviteResult?.claim_url}
                            </div>
                            <Button
                                type="button"
                                className="rounded-none bg-ikb hover:bg-[#001f7a] text-white w-full"
                                onClick={async () => {
                                    try { await navigator.clipboard.writeText(inviteResult.claim_url); toast.success("Copied to clipboard"); }
                                    catch { toast.error("Couldn't copy — select the link manually"); }
                                }}
                                data-testid="invite-copy-btn"
                            >
                                <Copy className="w-4 h-4 mr-2" /> Copy invite link
                            </Button>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" className="rounded-none" onClick={() => setInviteResult(null)} data-testid="invite-close-btn">Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {docsGateModal}
        </div>
    );
}

function ViewModeToggle({ viewMode, setViewMode }) {
    return (
        <div className="flex items-center justify-end mb-3" data-testid="page-view-toggle">
            <div className="inline-flex border border-border" role="tablist" aria-label="View mode">
                <button
                    type="button"
                    onClick={() => setViewMode("scroll")}
                    aria-pressed={viewMode === "scroll"}
                    className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 ${viewMode === "scroll" ? "bg-ikb text-white" : "bg-white hover:bg-secondary"}`}
                    data-testid="page-view-scroll"
                >Scroll</button>
                <button
                    type="button"
                    onClick={() => setViewMode("tabs")}
                    aria-pressed={viewMode === "tabs"}
                    className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 ${viewMode === "tabs" ? "bg-ikb text-white" : "bg-white hover:bg-secondary"}`}
                    data-testid="page-view-tabs"
                >Tabs</button>
            </div>
        </div>
    );
}

function CandidateQAPicker({ apps }) {
    const [activeId, setActiveId] = useState(apps[0]?.id || null);
    const active = apps.find((a) => a.id === activeId) || apps[0];
    return (
        <div className="space-y-4">
            {apps.length > 1 && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="qa-app-picker">
                    <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Application:</span>
                    {apps.map((a) => (
                        <button
                            key={a.id}
                            type="button"
                            onClick={() => setActiveId(a.id)}
                            className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 border ${active?.id === a.id ? "bg-ikb text-white border-ikb" : "bg-white border-border hover:bg-secondary"}`}
                            data-testid={`qa-app-${a.id}`}
                        >
                            {a.job_title || "Job"}
                        </button>
                    ))}
                </div>
            )}
            {active && <InterviewQA applicationId={active.id} />}
        </div>
    );
}



/** Phase 155 — Primary comms row: big tap targets for Call / WhatsApp /
 *  Email (compose) / Message (with unread badge). Replaces the tiny icon
 *  strip that cramped the mobile header.
 */
function PrimaryCommsRow({ c, unreadMsg, onMessage, onCompose }) {
    const digits = (c?.phone || "").replace(/\D/g, "");
    const logContact = (ch) => { api.post("/audit/contact", { candidate_id: c.id, channel: ch }).catch(() => {}); };
    return (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cand-primary-comms">
            {c?.phone ? (
                <a
                    href={`tel:${c.phone}`}
                    onClick={() => logContact("call")}
                    className="flex items-center justify-center gap-2 border-2 border-emerald-500 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-3 py-3 font-semibold text-sm transition-colors"
                    data-testid="cand-call-btn"
                    title={`Call ${c.phone}`}
                >
                    <Phone className="w-4 h-4" /> Call
                </a>
            ) : (
                <span className="flex items-center justify-center gap-2 border-2 border-dashed border-border bg-secondary/40 text-foreground/40 px-3 py-3 font-semibold text-sm">
                    <Phone className="w-4 h-4" /> Call
                </span>
            )}
            {c?.phone ? (
                <a
                    href={`https://wa.me/${digits}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => logContact("whatsapp")}
                    className="flex items-center justify-center gap-2 border-2 border-emerald-500 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-3 py-3 font-semibold text-sm transition-colors"
                    data-testid="cand-wa-btn"
                    title="Open WhatsApp"
                >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
            ) : (
                <span className="flex items-center justify-center gap-2 border-2 border-dashed border-border bg-secondary/40 text-foreground/40 px-3 py-3 font-semibold text-sm">
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                </span>
            )}
            {c?.email ? (
                <button
                    type="button"
                    onClick={() => { logContact("email"); onCompose?.(); }}
                    className={`relative flex items-center justify-center gap-2 border-2 px-3 py-3 font-semibold text-sm transition-colors ${
                        c.email_bounced
                            ? "border-rose-500 bg-rose-50 text-rose-700 hover:bg-rose-100"
                            : "border-ikb bg-blue-50 text-ikb hover:bg-blue-100"
                    }`}
                    data-testid="cand-email-btn"
                    title={c.email_bounced
                        ? `Last send bounced: ${c.email_bounce_reason || "address rejected"}`
                        : `Compose email to ${c.email} — template or custom`}
                >
                    <Mail className="w-4 h-4" /> Email
                    {c.email_bounced && (
                        <span
                            className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 bg-rose-600 text-white text-[9px] font-bold leading-[18px] text-center rounded-full border-2 border-white"
                            data-testid="cand-email-bounced-badge"
                            aria-label="This email address bounced"
                        >
                            !
                        </span>
                    )}
                </button>
            ) : (
                <span className="flex items-center justify-center gap-2 border-2 border-dashed border-border bg-secondary/40 text-foreground/40 px-3 py-3 font-semibold text-sm">
                    <Mail className="w-4 h-4" /> Email
                </span>
            )}
            <button
                type="button"
                onClick={onMessage}
                className="relative flex items-center justify-center gap-2 border-2 border-indigo-500 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 px-3 py-3 font-semibold text-sm transition-colors"
                data-testid="cand-message-btn"
                title="In-portal conversation"
            >
                <MessageSquare className="w-4 h-4" /> Message
                {unreadMsg > 0 && (
                    <span
                        className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 bg-rose-600 text-white text-[10px] font-bold leading-5 text-center rounded-full border-2 border-white"
                        data-testid="cand-message-unread-badge"
                    >
                        {unreadMsg > 99 ? "99+" : unreadMsg}
                    </span>
                )}
            </button>
        </div>
    );
}

/** Phase 155 — frequently used ATS actions (Add to job / list / Schedule /
 *  Resume / Invite / Offers / Mark unfit). Flex-wraps nicely on mobile.
 */
function SecondaryActionsRow({ c, onResume, onLoaded, onListsLoaded, inviting, setInviting, setInviteResult }) {
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [newSlot, setNewSlot] = useState(null);

    const scheduleInterview = async () => {
        try {
            const start = new Date();
            start.setDate(start.getDate() + 1);
            start.setHours(10, 0, 0, 0);
            const { data } = await api.post("/calendar/interviews", {
                candidate_id: c.id,
                round_name: "Interview",
                scheduled_at: start.toISOString(),
                duration_minutes: 60,
                online_offline: "online",
                mode: "Video",
            });
            setNewSlot(data);
            setScheduleOpen(true);
            toast.success("Interview created — confirm time and invitees.");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <>
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="cand-secondary-actions">
                <AssignJobsButton candidateId={c.id} onAssigned={onLoaded} />
                <AddToListButton candidateId={c.id} onAdded={onListsLoaded} />
                <button
                    type="button"
                    onClick={scheduleInterview}
                    className="text-[11px] font-mono uppercase tracking-widest border border-ikb bg-ikb text-white px-2.5 py-1.5 hover:bg-[#001f7a] inline-flex items-center gap-1"
                    data-testid="cand-schedule-int-btn"
                >
                    <CalIcon className="w-3 h-3" /> Schedule
                </button>
                {c.resume_filename && (
                    <button
                        type="button"
                        onClick={onResume}
                        className="text-[11px] font-mono uppercase tracking-widest border border-border bg-white px-2.5 py-1.5 hover:bg-secondary inline-flex items-center gap-1"
                        data-testid="cand-resume-link"
                        title={c.resume_filename}
                    >
                        <Eye className="w-3 h-3" /> Resume
                    </button>
                )}
                {c.email && !c.seeker_id && (
                    <button
                        type="button"
                        onClick={async () => {
                            setInviting(true);
                            try {
                                const { data } = await api.post(`/candidates/${c.id}/invite-to-portal`);
                                setInviteResult(data);
                                onLoaded?.();
                            } catch (err) { toast.error(formatApiError(err)); }
                            finally { setInviting(false); }
                        }}
                        disabled={inviting}
                        className="text-[11px] font-mono uppercase tracking-widest border border-border bg-white px-2.5 py-1.5 hover:bg-secondary inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid="cand-invite-portal-btn"
                    >
                        <UserPlus className="w-3 h-3" /> {inviting ? "…" : "Invite"}
                    </button>
                )}
                <Link
                    to={`/candidates/${c.id}/offers`}
                    className="text-[11px] font-mono uppercase tracking-widest border border-ikb text-ikb bg-white px-2.5 py-1.5 hover:bg-blue-50 inline-flex items-center gap-1"
                    data-testid="cand-offers-link"
                >
                    Offers →
                </Link>
                <UnfitToggleButton candidate={c} onChanged={onLoaded} />
            </div>
            {scheduleOpen && newSlot && (
                <InterviewDetailDialog
                    open={scheduleOpen}
                    interview={newSlot}
                    onClose={() => { setScheduleOpen(false); setNewSlot(null); }}
                    onSaved={() => { onLoaded?.(); }}
                />
            )}
        </>
    );
}

/** Phase 155 — Rarely used verify/export actions. Collapsed under a "More"
 *  button on mobile so the header isn't a wall of chips.
 */
function MoreActionsRow({ c, onLoaded }) {
    const [open, setOpen] = useState(false);
    const Chips = (
        <>
            <a
                href={`${API}/candidates/${c.id}/verification-packet.pdf`}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] font-mono uppercase tracking-widest border border-slate-300 text-slate-700 bg-white px-2.5 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1"
                data-testid="cand-verification-packet"
                title="Download a single PDF combining BGV + reference calls + AI scan + comp benchmark"
            >
                <Download className="w-3 h-3" /> Verification packet
            </a>
            <button
                type="button"
                onClick={async () => {
                    const t = toast.loading("Scanning every uploaded letter…");
                    try {
                        const { data } = await api.post(`/candidates/${c.id}/verify-all-documents`);
                        toast.success(
                            `Scanned ${data.scanned}: ${data.summary.verified} verified · ${data.summary.red_flag} red · ${data.summary.minor} minor · ${data.summary.unreadable} unreadable`,
                            { id: t },
                        );
                        await onLoaded?.();
                    } catch (err) { toast.error(formatApiError(err), { id: t }); }
                }}
                className="text-[11px] font-mono uppercase tracking-widest border border-emerald-400 text-emerald-700 bg-white px-2.5 py-1.5 hover:bg-emerald-50 inline-flex items-center gap-1"
                data-testid="cand-verify-all-docs"
                title="Run AI red-flag scan across every uploaded joining / relieving / experience letter"
            >
                <Sparkles className="w-3 h-3" /> Verify all docs
            </button>
            <a
                href={`${API}/candidates/${c.id}/client-summary`}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] font-mono uppercase tracking-widest border border-border bg-white px-2.5 py-1.5 hover:bg-secondary inline-flex items-center gap-1"
                data-testid="cand-client-summary-btn"
                title="Client-shareable summary (last name masked)"
            >
                <Printer className="w-3 h-3" /> Client PDF
            </a>
            <a
                href={`${API}/candidates/${c.id}/masked-resume`}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] font-mono uppercase tracking-widest border border-amber-400 bg-amber-50 text-amber-800 px-2.5 py-1.5 hover:bg-amber-100 inline-flex items-center gap-1"
                data-testid="cand-masked-resume-btn"
                title="Verbatim resume with contact info, social links & surname masked — open then Print → Save as PDF"
            >
                <Printer className="w-3 h-3" /> Masked resume
            </a>
        </>
    );
    return (
        <div className="pt-1 border-t border-dashed border-border" data-testid="cand-more-actions">
            {/* Mobile: collapsible */}
            <div className="sm:hidden">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="w-full text-[11px] font-mono uppercase tracking-widest border border-border bg-white px-2.5 py-2 inline-flex items-center justify-center gap-1 hover:bg-secondary"
                    data-testid="cand-more-toggle"
                    aria-expanded={open}
                >
                    {open ? <X className="w-3 h-3" /> : <MoreHorizontal className="w-3 h-3" />}
                    {open ? "Hide extras" : "More actions"}
                </button>
                {open && <div className="flex flex-wrap items-center gap-1.5 pt-2">{Chips}</div>}
            </div>
            {/* Desktop: always visible */}
            <div className="hidden sm:flex flex-wrap items-center gap-1.5 pt-2">{Chips}</div>
        </div>
    );
}


function UnfitToggleButton({ candidate, onChanged }) {
    const { user } = useAuth();
    const [busy, setBusy] = useState(false);
    const isUnfit = !!candidate?.unfit;
    if (!user || !["employer", "recruiter"].includes(user.role)) return null;

    const toggle = async () => {
        if (busy) return;
        setBusy(true);
        try {
            if (isUnfit) {
                await api.delete(`/candidates/${candidate.id}/unfit`);
                toast.success("Cleared unfit mark");
            } else {
                const reason = window.prompt("Reason for marking unfit (optional):", "") || "";
                await api.post(`/candidates/${candidate.id}/unfit`, { unfit: true, reason });
                toast.success("Marked unfit — hidden from leaderboards");
            }
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || err.message);
        } finally { setBusy(false); }
    };

    return (
        <button
            type="button"
            onClick={toggle}
            disabled={busy}
            className={`text-[11px] font-mono uppercase tracking-widest border px-2.5 py-1.5 inline-flex items-center gap-1 disabled:opacity-60 ${
                isUnfit
                    ? "border-rose-500 bg-rose-500 text-white hover:bg-rose-600"
                    : "border-border bg-white hover:border-rose-300 hover:text-rose-700"
            }`}
            data-testid="cand-unfit-toggle"
            title={isUnfit ? "Click to clear unfit mark" : "Mark this candidate as unfit across all applications"}
        >
            {isUnfit ? "✓ Unfit" : "Mark unfit"}
        </button>
    );
}

/** HeaderStatStrip — 3 compact pill groups shown right under the meta row:
 *  1. AI Score (best `match_score` across applications) | Manual Score
 *     (best `recruiter_score` across applications — recruiter override).
 *  2. Current CTC → Expected CTC (from candidate.compensation)
 *  Any missing datum is silently hidden.
 *
 *  Note: backend stores the recruiter override on `application.recruiter_score`
 *  (see routers/applications.py). Older code paths referred to it as
 *  `manual_score`; we resolve either so we're forward-compatible.
 */
function HeaderStatStrip({ candidate: c, firstApp }) {
    const apps = c?.applications || [];
    const aiScores = apps.map((a) => typeof a?.match_score === "number" ? a.match_score : null).filter((v) => v != null);
    const manualScores = apps.map((a) => {
        const v = typeof a?.recruiter_score === "number" ? a.recruiter_score
            : typeof a?.manual_score === "number" ? a.manual_score
            : null;
        return v;
    }).filter((v) => v != null);
    const aiScore = aiScores.length ? Math.round(Math.max(...aiScores))
        : typeof firstApp?.match_score === "number" ? Math.round(firstApp.match_score) : null;
    const manualScore = manualScores.length ? Math.round(Math.max(...manualScores))
        : typeof c?.manual_score === "number" ? Math.round(c.manual_score) : null;
    const comp = c?.compensation || {};
    const currentCtc = comp.current_ctc || c.current_ctc;
    const expectedCtc = comp.expected_ctc || c.expected_ctc;
    const currency = comp.currency || "INR";
    const fmt = (n) => n != null ? new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(n)) : null;
    // Roll-up BGV progress from the embedded background_check doc — cheap,
    // no extra fetch. Only rendered when there is at least one check
    // touched (started_at set) so we don't clutter the header for
    // untouched candidates.
    const bgv = c?.background_check;
    let bgvProgress = null;
    if (bgv?.started_at) {
        const checks = bgv.checks || {};
        let total = 0, done = 0, flagged = 0;
        Object.entries(checks).forEach(([_, st]) => {
            if (!st) return;
            const s = st.status || "not_started";
            if (s === "not_applicable") return;
            total++;
            if (s === "clear" || s === "flagged") done++;
            if (s === "flagged") flagged++;
        });
        bgvProgress = { total, done, flagged, pct: total ? Math.round((done / total) * 100) : 0 };
    }
    const anyScore = aiScore != null || manualScore != null;
    const anySalary = currentCtc != null || expectedCtc != null;
    if (!anyScore && !anySalary && !bgvProgress) return null;
    const scoreCls = (s) => s >= 75 ? "border-emerald-500 text-emerald-700 bg-emerald-50"
        : s >= 50 ? "border-amber-500 text-amber-700 bg-amber-50"
        : "border-rose-500 text-rose-700 bg-rose-50";
    const bgvCls = bgvProgress?.flagged > 0
        ? "border-rose-400 text-rose-800 bg-rose-50"
        : (bgvProgress?.pct === 100 ? "border-emerald-500 text-emerald-800 bg-emerald-50" : "border-blue-400 text-blue-800 bg-blue-50");
    return (
        <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="cand-header-stats">
            {aiScore != null && (
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border inline-flex items-center gap-1.5 ${scoreCls(aiScore)}`} data-testid="cand-header-ai-score">
                    AI <b className="tabular-nums text-sm">{aiScore}</b>
                </span>
            )}
            {manualScore != null && (
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border inline-flex items-center gap-1.5 ${scoreCls(manualScore)}`} data-testid="cand-header-manual-score">
                    Manual <b className="tabular-nums text-sm">{manualScore}</b>
                </span>
            )}
            {bgvProgress && (
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border inline-flex items-center gap-1.5 ${bgvCls}`} data-testid="cand-header-bgv" title="BGV checks marked N/A are excluded from the total">
                    BGV <b className="tabular-nums text-sm">{bgvProgress.done}/{bgvProgress.total}</b>
                    {bgvProgress.flagged > 0 && <span className="normal-case ml-1">· {bgvProgress.flagged} flagged</span>}
                </span>
            )}
            {anySalary && (
                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-border bg-white inline-flex items-center gap-1.5" data-testid="cand-header-salary">
                    Salary
                    <b className="tabular-nums text-foreground normal-case">{fmt(currentCtc) || "—"}</b>
                    <span className="text-muted-foreground">→</span>
                    <b className="tabular-nums text-ikb normal-case">{fmt(expectedCtc) || "—"}</b>
                </span>
            )}
        </div>
    );
}

